import { TrashIcon } from '@heroicons/react/24/outline'
import { type ChosenPayee, type LoanParty, PartyPayeeLookup } from 'components/PartyPayeeLookup'
import type { ReactNode } from 'react'
import { Input2 } from 'stories/components'
import { removeComma, thousandSeperatorWithPrefix } from 'utils'

/**
 * One "pay others" line: extra money collected on a payment and passed through to a third party.
 * Mirrors the backend OtherPaymentLine / CreatePayableDto shape. `amount` is kept as the raw input
 * string while editing; the backend parses it (removeCommaN) when the payment is applied.
 *
 * `payeeType` / `payeeRefId` are what tie the line to a real entity, so the payout queued when the
 * payment is applied is a distribution to that party rather than a name on a check.
 */
export interface OtherPaymentLine {
  payeeType: string
  payeeRefId: number | null
  payeeName: string
  payeeAddress?: string
  amount: number | string
  memo?: string
}

const emptyLine = (): OtherPaymentLine => ({
  payeeType: 'Other',
  payeeRefId: null,
  payeeName: '',
  payeeAddress: '',
  amount: '',
  memo: '',
})

/** Total of the lines, coercing the raw amount strings to numbers. */
export const sumOtherPayments = (rows: OtherPaymentLine[] = []): number =>
  (rows || []).reduce((sum, r) => sum + Number(removeComma(r?.amount ?? 0) || 0), 0)

/** The row's saved payee in the shape the lookup shows, or null while the row is still blank. */
const chosenFromRow = (row: OtherPaymentLine): ChosenPayee | null =>
  row.payeeName
    ? {
        payeeType: row.payeeType || 'Other',
        payeeRefId: row.payeeRefId ?? null,
        name: row.payeeName,
        address: row.payeeAddress || '',
      }
    : null

/**
 * Add/edit table of extra payees paid out of a payment. Used on the Loan Payment screen and on the
 * Servicing Information page (to configure per-loan defaults). Each row picks who is being paid
 * with the same lookup the Pay a Party screen uses — parties on the loan, any vendor in the
 * accounting book, or a one-off name — and an amount; the running total is shown so it can be
 * folded into the amount collected.
 *
 * `action` and `footer` let the owning screen say what these lines mean to it without this editor
 * having to know: the payment screen uses them for the payoff overage refund, which is raised as an
 * ordinary line here but is money leaving a bucket at the top of that screen rather than money the
 * payment collects.
 */
export const OtherPaymentsEditor = ({
  value,
  onChange,
  onCommit,
  payees,
  disabled = false,
  title = 'Other Payments',
  hint = 'Extra amounts collected on this payment and paid through to these payees.',
  action,
  footer,
}: {
  value: OtherPaymentLine[]
  onChange: (rows: OtherPaymentLine[]) => void
  /** Fired when a change should be persisted (row added/removed, payee picked, field blurred). */
  onCommit?: (rows: OtherPaymentLine[]) => void
  payees: LoanParty[]
  disabled?: boolean
  title?: string
  hint?: string
  /** Shown beside "+ Add payee": a one-click line the owning screen can offer. */
  action?: ReactNode
  /** Shown under the running total, for whatever the owning screen has to say about the lines. */
  footer?: ReactNode
}) => {
  const rows = Array.isArray(value) ? value : []

  const update = (index: number, patch: Partial<OtherPaymentLine>) => {
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)))
  }
  // Live edit + immediate persist (used by picks and structural changes).
  const commit = (next: OtherPaymentLine[]) => {
    onChange(next)
    onCommit?.(next)
  }
  const updateAndCommit = (index: number, patch: Partial<OtherPaymentLine>) => {
    commit(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)))
  }
  const addRow = () => commit([...rows, emptyLine()])
  const removeRow = (index: number) => commit(rows.filter((_, i) => i !== index))

  const total = sumOtherPayments(rows)

  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-1">
        <p className="font-semibold text-sm">{title}</p>
        {!disabled && (
          <div className="flex items-center gap-3">
            {action}
            <span className="cursor-pointer text-shade-blue hover:underline text-sm" onClick={addRow}>
              + Add payee
            </span>
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-2">{hint}</p>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-400 italic">No other payments.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((row, index) => (
            <div className="grid grid-cols-12 gap-2 items-end" key={index}>
              <div className="col-span-5">
                <PartyPayeeLookup
                  parties={payees}
                  value={chosenFromRow(row)}
                  disabled={disabled}
                  // An amount going nowhere would be dropped on save, so say so on the row itself.
                  error={
                    !row.payeeName && Number(removeComma(row.amount ?? 0) || 0) > 0
                      ? 'Choose who is being paid'
                      : ''
                  }
                  onSelect={(payee) => {
                    // null means the typed text no longer matches the saved payee; the row keeps
                    // what it had until something is actually picked.
                    if (!payee) return
                    updateAndCommit(index, {
                      payeeName: payee.name,
                      payeeType: payee.payeeType,
                      payeeRefId: payee.payeeRefId,
                      payeeAddress: payee.address,
                    })
                  }}
                />
              </div>
              <div className="col-span-3">
                <Input2
                  title="Amount"
                  type="thousandSep"
                  prefix="$"
                  value={String(row.amount ?? '')}
                  disabled={disabled}
                  onChange={(v: string) => update(index, { amount: v })}
                  onBlur={() => onCommit?.(rows)}
                />
              </div>
              <div className="col-span-3">
                <Input2
                  title="Memo"
                  value={row.memo || ''}
                  disabled={disabled}
                  onChange={(v: string) => update(index, { memo: v })}
                  onBlur={() => onCommit?.(rows)}
                />
              </div>
              <div className="col-span-1 flex justify-center pb-2">
                {!disabled && (
                  <TrashIcon
                    className="w-5 h-5 text-red-500 cursor-pointer"
                    onClick={() => removeRow(index)}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {rows.length > 0 && (
        <div className="flex justify-end mt-2 text-sm font-medium">
          <span className="text-gray-500 mr-2">Total other payments:</span>
          <span>{thousandSeperatorWithPrefix(total, '$')}</span>
        </div>
      )}

      {footer}
    </div>
  )
}
